import type { Addon, ComboChoice } from '@/shared/lib/db/schemas/cart.schema';
import type { ComboGroupOption, StoreItem } from '@/modules/stores/types/get-store-items';
import { getOptionAdditionalPrice } from '@/modules/stores/utils/combo';

import type { Order } from '../types/order';

type OrderItem = Order['items'][number];
type OrderItemAddon = OrderItem['order_item_addons'][number];

/**
 * Id real do complemento da linha gravada. A rota `api/app/orders` devolve
 * `addon_id`; tipos/fixtures antigos usam `item_addon_id`. Linhas de exibição
 * das escolhas de combo (PRAT-20) não têm id e voltam `null`.
 */
export function getOrderAddonId(addon: OrderItemAddon): string | null {
  return addon.item_addon_id || addon.addon_id || null;
}

/**
 * `true` quando a linha do pedido é um combo com escolhas (PRAT-20): traz
 * `combo_choices` ou linhas de exibição de escolha (complemento sem id).
 */
export function isComboOrderItem(orderItem: OrderItem): boolean {
  return (
    (orderItem.combo_choices?.length ?? 0) > 0 ||
    (orderItem.order_item_addons ?? []).some(addon => !getOrderAddonId(addon))
  );
}

function findOption(
  catalogItem: StoreItem | undefined,
  groupId: string | undefined,
  groupName: string,
  itemId: string | undefined,
  itemName: string
): { groupId: string; option: ComboGroupOption } | null {
  for (const group of catalogItem?.combo_groups ?? []) {
    if (groupId ? group.id !== groupId : group.name !== groupName) continue;
    const option = group.options.find(opt =>
      itemId ? opt.item_id === itemId : opt.name === itemName
    );
    if (option) return { groupId: group.id, option };
  }
  return null;
}

function divideIfExact(value: number, divisor: number): number {
  return divisor > 1 && value % divisor === 0 ? value / divisor : value;
}

/**
 * Reconstrói as escolhas de uma linha de combo de um pedido gravado (PRAT-20),
 * no formato do carrinho, para refazer o pedido ou reenviá-lo (troca de forma
 * de pagamento). Segue o contrato (seção 4):
 *
 * - escolha: `combo_choices` do pedido (group_id = combo_group_id, item_id); sem
 *   ele, a linha de exibição (complemento sem id: categoria = grupo, nome =
 *   produto) casada por nome com o cardápio atual;
 * - quantidade por unidade do combo: a da linha de exibição; senão a gravada ÷
 *   a quantidade da opção no cardápio (quando exata); senão a gravada;
 * - complementos da escolha: linhas COM id cuja categoria começa com
 *   "<produto> · ", com a quantidade gravada ÷ a quantidade da escolha.
 *
 * Linhas de exibição nunca viram complemento. Escolhas que não se consegue
 * identificar ficam de fora (a API recusa o combo incompleto com 422).
 */
export function rebuildComboChoices(orderItem: OrderItem, catalogItem?: StoreItem): ComboChoice[] {
  const rows = orderItem.order_item_addons ?? [];
  const displayRows = rows.filter(row => !getOrderAddonId(row));

  type Base = Omit<ComboChoice, 'addons'> & { option: ComboGroupOption | null };
  const bases: Base[] = [];

  if (orderItem.combo_choices?.length) {
    for (const stored of orderItem.combo_choices) {
      const found = findOption(
        catalogItem,
        stored.combo_group_id,
        stored.combo_group_name,
        stored.item_id,
        stored.item_name
      );
      const displayRow = displayRows.find(
        row =>
          row.addon_category_name === stored.combo_group_name && row.addon_name === stored.item_name
      );
      const quantity = displayRow
        ? Number(displayRow.addon_quantity)
        : divideIfExact(Number(stored.quantity), Number(found?.option.quantity) || 1);

      bases.push({
        group_id: stored.combo_group_id,
        group_name: stored.combo_group_name,
        item_id: stored.item_id,
        name: stored.item_name,
        quantity: quantity > 0 ? quantity : 1,
        additional_price: Number(stored.additional_price) || 0,
        option: found?.option ?? null,
      });
    }
  } else {
    for (const row of displayRows) {
      const found = findOption(
        catalogItem,
        undefined,
        row.addon_category_name,
        undefined,
        row.addon_name
      );
      if (!found) continue;

      bases.push({
        group_id: found.groupId,
        group_name: row.addon_category_name,
        item_id: found.option.item_id,
        name: found.option.name,
        quantity: Number(row.addon_quantity) > 0 ? Number(row.addon_quantity) : 1,
        additional_price: getOptionAdditionalPrice(found.option),
        option: found.option,
      });
    }
  }

  return bases.map(({ option, ...base }) => {
    const prefix = `${base.name} · `;
    const categories = option?.addon_categories ?? [];

    const addons: Addon[] = rows
      .filter(row => getOrderAddonId(row) && row.addon_category_name?.startsWith(prefix))
      .map(row => {
        const id = getOrderAddonId(row)!;
        const category = categories.find(cat => cat.addons.some(addon => addon.id === id));
        const catalogAddon = category?.addons.find(addon => addon.id === id);

        return {
          id,
          name: row.addon_name,
          // Preço do cardápio: nas regras "maior preço"/"média" a linha gravada
          // traz o valor rateado (0 nas não cobradas).
          price: Number(catalogAddon?.price ?? row.addon_price) || 0,
          quantity: divideIfExact(Number(row.addon_quantity) || 1, base.quantity),
          addon_category_id: category?.id ?? '',
          consider_most_expensive: category?.consider_most_expensive,
          price_calculation_type: category?.price_calculation_type,
          select_max: category?.select_max,
          item_select_max: catalogAddon?.select_max,
        };
      });

    return { ...base, addons };
  });
}
